// ============================================================================
// Bid Sheets — client-side data store (backend-backed)
// ----------------------------------------------------------------------------
// The store is the single seam between the estimator UI and the /bids REST API
// (P1/P2). Components keep the SAME synchronous read interface
// (getBids/getBid + useSyncExternalStore); the store holds an in-memory cache
// that it hydrates from and writes through to the backend via `bidService`.
//
//   - getBids/getBid          -> read the cache synchronously (stable refs)
//   - hydrate/ensureWorkbook  -> pull from the backend (list headers / a bid's workbook)
//   - update/delete/setBidWorkbook -> optimistic cache update + async persist
//   - createDraftBid          -> server-first create (awaited; the server id wins)
//
// The awkward mapping (bid-number id <-> UUID, camel<->snake, the row_length/
// profit split) lives in bidService.ts; this file owns the cache + bid-number->UUID map.
// ============================================================================

import type { BidWorkbook } from './BidDetail/bidWorkbook';
import * as api from './bidService';
import type { StoredBid } from './bidService';
import { listCatalog } from './Admin/referenceDataService';
import { money } from './financialDisplay';
import { setSaveStatus } from './bidSaveStatus';
import ToastService from '../AppSettings/services/ToastService';

/**
 * Surface a rejected write instead of swallowing it.
 *
 * Writes are optimistic, so a silent failure is the worst outcome: the user sees their edit stick,
 * gets no feedback, and loses it on reload — and a role-gated 403 (P4: bid mutations require the
 * MUTATE_BIDS capability) is exactly the case that produces it routinely.
 */
function reportWriteFailure(action: string, err: unknown): void {
  const status = (err as { status?: number } | null)?.status;
  const message = (err as { message?: string } | null)?.message;
  ToastService.error(
    status === 403
      ? `You do not have permission to ${action}.`
      // A 409 is the server refusing on STATE — the status edit-lock names the status and the
      // way back, and that reason must reach the user verbatim, not a generic shrug.
      : status === 409 && message
        ? message
        : `Could not ${action}. Your change was not saved.`,
  );
  console.warn(`Bid write failed (${action}):`, err);
}

export type BidStatus = 'Draft' | 'In Review' | 'Submitted' | 'Won' | 'Lost';

export interface Bid {
  /** Bid number, e.g. "BID-2026-014" (the API addresses bids by UUID; the store maps between them) */
  id: string;
  /** Bid / project name */
  name: string;
  /** Client company, free text — what the bid list and header display. */
  client: string;
  /**
   * The `clients` row this bid belongs to: a PERSON at that company, since `clients` is one row per
   * contact and several rows routinely share a company. Conversion files the project against this
   * person, so without it a bid for a company with more than one contact cannot become a project.
   * '' when unset (legacy bids, and bids started before the contact is known).
   */
  clientContactId?: string;
  /** The project this bid was converted into (Part II P1). Undefined until it is converted. */
  convertedMatterid?: string;
  area: string;
  /** Estimator / bid leader */
  estimator: string;
  status: BidStatus;
  /** Total bid (contract) value in dollars — server-authoritative (computed_bid_total) */
  /** undefined when the server omitted it (a financially-blind caller) — never coerced to 0. */
  bidValue?: number;
  /** Target margin, 0..1 fraction (the cover input speaks percent). Pricing posture —
   *  undefined when the server omitted it (a financially-blind caller) or it is unrecorded;
   *  null in a PATCH = an explicit clear (the discountPct treatment). */
  targetMargin?: number | null;
  /** Pipe / scope spec, e.g. '10 km of 10" pipe' */
  scope: string;
  /** ISO date the bid was created/opened */
  createdDate: string;
  /** ISO date the bid is due to the client */
  dueDate: string;
  description?: string;
  // ── COVER outcome block (gap 3.7) ──
  /** Who the job was awarded to (may be us). '' = not recorded. */
  awardedTo: string;
  /** The award figure — author-supplied money, stripped for blind callers exactly like bidValue.
   *  undefined = absent (unrecorded OR stripped); null in a PATCH = an explicit clear. */
  awardedValue?: number | null;
  /** How many bidders there were. A fact, not money — survives stripping. */
  bidderCount?: number | null;
  /** Server-DERIVED analytics (awarded − our bid; the per-metre trio). Read-only: never sent
   *  back — the server computes them from its own authoritative rollups at every read. */
  awardDiff?: number;
  awardedPerM?: number;
  awardDiffPerM?: number;
  // ── COVER administrative block (gap 3.8) ──
  bidMeetingDate: string;
  /** Our rep at the bid meeting (the workbook COVER's "MSI Rep"). */
  companyRep: string;
  /** The other bidders as recorded — free text, independent of bidderCount (never derived). */
  otherBidders: string;
  /** Tri-state: undefined = unrecorded (not "no"); null in a PATCH clears back to unrecorded. */
  bondsRequired?: boolean | null;
  /** Expected job start / completion, per the COVER. */
  startDate: string;
  completionDate: string;
  /** The client discount (Tier 4.4): RECORDED negotiation fact, applied to nothing. Money
   *  (pricing posture) — absent when stripped or unrecorded; null in a PATCH clears. */
  discountPct?: number | null;
  /** Structured estimate (phases, equipment, labour) behind the bid. Loaded lazily per bid. */
  workbook?: BidWorkbook;
}

export const BID_STATUSES: BidStatus[] = [
  'Draft',
  'In Review',
  'Submitted',
  'Won',
  'Lost',
];

// ---------------------------------------------------------------------------
// In-memory cache + bid-number -> UUID map (the store's private state)
// ---------------------------------------------------------------------------
let cache: Bid[] = [];
const idMap = new Map<string, string>(); // bid-number -> server UUID
let hydrated = false;
let loadFailed = false;
let hydrating: Promise<void> | null = null;

// Lightweight pub/sub so React views re-render when the cache changes.
type Listener = () => void;
const listeners = new Set<Listener>();
function emit(): void {
  listeners.forEach((l) => l());
}

/** Replace the cache with a new array reference (never mutate in place) + notify. */
function commit(next: Bid[]): void {
  cache = next;
  emit();
}

/** Upsert a server bid into the cache, preserving any already-loaded workbook. */
function absorb(stored: StoredBid): void {
  idMap.set(stored.bid.id, stored.uuid);
  const i = cache.findIndex((b) => b.id === stored.bid.id);
  if (i >= 0) {
    const next = cache.slice();
    next[i] = { ...cache[i], ...stored.bid }; // keep workbook already in cache
    commit(next);
  } else {
    commit([stored.bid, ...cache]);
  }
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  // First observer triggers a load; subsequent ones reuse the cache.
  if (!hydrated && !hydrating) void hydrate();
  return () => {
    listeners.delete(listener);
  };
}

// ---------------------------------------------------------------------------
// Reads (synchronous — the cache is the source for render)
// ---------------------------------------------------------------------------
export function getBids(): Bid[] {
  return cache;
}

export function getBid(id: string): Bid | undefined {
  return cache.find((b) => b.id === id);
}

/** True once the initial list load has completed (used to tell "loading" from "not found"). */
/** The server UUID for a bid-number, once hydrated. Needed by routes the API addresses by UUID
 *  (e.g. /bids/<uuid>/rate-card) while the UI routes by bid-number. */
export function getBidUuid(id: string): string | undefined {
  return idMap.get(id);
}

export function isHydrated(): boolean {
  return hydrated;
}

/** True when the last list load FAILED — the list page must say so, not render "No bids found"
 * (a network failure and an empty tenant are different answers; gap review 0.11). Cleared by any
 * later successful hydrate/refresh. */
export function hydrateFailed(): boolean {
  return loadFailed;
}

// ---------------------------------------------------------------------------
// Loads
// ---------------------------------------------------------------------------
export function hydrate(): Promise<void> {
  if (hydrating) return hydrating;
  hydrating = (async () => {
    try {
      const stored = await api.listBids();
      idMap.clear();
      stored.forEach((s) => idMap.set(s.bid.id, s.uuid));
      commit(stored.map((s) => s.bid));
      hydrated = true;
      loadFailed = false;
    } catch (err) {
      console.warn('Failed to load bids:', err);
      loadFailed = true;
      emit(); // subscribers re-render into the error state, not an eternal spinner/empty list
    } finally {
      hydrating = null;
    }
  })();
  return hydrating;
}

/** Force a fresh list load (e.g. after an out-of-band change). */
export function refresh(): Promise<void> {
  hydrated = false;
  return hydrate();
}

/**
 * Ensure a bid's workbook is present in the cache. The list load only fetches headers,
 * so the detail view calls this to lazily pull `{rowLengthM, profitPct, phases}`.
 */
/** Bid ids whose LAST workbook fetch failed. While an id is in here the persisted workbook is
 * UNKNOWN — not empty — so writes are refused (see setBidWorkbook): the editor's empty fallback
 * plus autosave would otherwise replace the real workbook moments after the first edit
 * (gap review 0.4 — a transient network failure was one edit away from silent data loss). */
const workbookLoadFailed = new Set<string>();

/** True while `id`'s workbook could not be loaded — the page must offer Retry, not an editor. */
export function workbookLoadFailedFor(id: string): boolean {
  return workbookLoadFailed.has(id);
}

export async function ensureWorkbook(id: string): Promise<boolean> {
  if (!hydrated && !hydrating) await hydrate();
  else if (hydrating) await hydrating;
  const uuid = idMap.get(id);
  if (!uuid) return false; // unknown bid (e.g. deep-link to a non-existent id)
  if (getBid(id)?.workbook) {
    workbookLoadFailed.delete(id); // already loaded (or a fresh draft's empty workbook)
    return true;
  }
  try {
    const workbook = await api.fetchWorkbook(uuid);
    const i = cache.findIndex((b) => b.id === id);
    if (i >= 0) {
      const next = cache.slice();
      next[i] = { ...cache[i], workbook };
      commit(next);
    }
    workbookLoadFailed.delete(id);
    return true;
  } catch (err) {
    console.warn('Failed to load workbook:', err);
    workbookLoadFailed.add(id);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Writes — optimistic cache update NOW, debounced + SERIALISED write-through.
// ---------------------------------------------------------------------------
//
// The cache is always updated synchronously so the editor stays live. The
// network write is deferred and funnelled through a per-bid queue that
// guarantees three things the old "fire a PUT per edit" path did not:
//
//   1. DEBOUNCE — a burst of edits (typing, walkthrough, a rush of structural
//      changes) collapses into ONE save `SAVE_DEBOUNCE_MS` after the last edit,
//      instead of a request per keystroke.
//   2. SERIAL   — at most one request per bid is ever in flight. The old code
//      fired every write as its own detached promise, so responses could land
//      out of order and a slow server got hammered with overlapping replace-all
//      workbook PUTs ("the system can't save it that quick").
//   3. COALESCE — edits made WHILE a save is in flight are merged into the next
//      save (header patches merge field-by-field, the workbook is latest-wins,
//      which is correct because the workbook PUT is replace-all). Nothing is
//      lost and nothing races.
//
// A header patch and a workbook save are always applied header-first within a
// single drain, so profit/row-length (which ride the header) never clobber a
// workbook that was queued alongside them.
// ---------------------------------------------------------------------------

/** How long after the last edit an autosave fires. */
export const SAVE_DEBOUNCE_MS = 5000;

interface PendingSave {
  /** Merged, not-yet-sent header patch (field-by-field, last write wins). */
  header: Partial<Bid>;
  /** Latest not-yet-sent workbook (replace-all, so latest wins), or null. */
  workbook: BidWorkbook | null;
  /** Optimistic total that went with `workbook`; reconciled to the server's. */
  workbookTotal?: number;
  /** The debounce timer, or null when none is scheduled. */
  timer: ReturnType<typeof setTimeout> | null;
  /** True while this bid's save loop is running — the serialisation guard. */
  inFlight: boolean;
}

const saves = new Map<string, PendingSave>();

function pendingFor(id: string): PendingSave {
  let p = saves.get(id);
  if (!p) {
    p = { header: {}, workbook: null, workbookTotal: undefined, timer: null, inFlight: false };
    saves.set(id, p);
  }
  return p;
}

function hasWork(p: PendingSave): boolean {
  return Object.keys(p.header).length > 0 || p.workbook !== null;
}

/** Recompute the aggregate indicator status across every bid's queue. */
function refreshSaveStatus(): void {
  let anyFlight = false;
  let anyPending = false;
  saves.forEach((p) => {
    if (p.inFlight) anyFlight = true;
    // A debounce timer is only ever armed together with pending work (see scheduleSave), so
    // `hasWork` is the authority. Keying off `p.timer` instead left the indicator stuck on
    // "Unsaved changes" after a mid-flight-coalesced edit drained: the drain saved the work but a
    // stale timer handle lingered, so this reported pending forever.
    if (hasWork(p)) anyPending = true;
  });
  if (anyFlight) setSaveStatus('saving');
  else if (anyPending) setSaveStatus('pending');
  // else: leave the status alone — a drained queue is reported as 'saved' by the
  // save loop itself, and we must not stomp that (or an 'error') back to idle.
}

/** (Re)start the debounce for a bid. */
function scheduleSave(id: string): void {
  const uuid = idMap.get(id);
  if (!uuid) return; // no server row yet (optimistic-only) — cache is the record
  const p = pendingFor(id);
  if (p.timer) clearTimeout(p.timer);
  p.timer = setTimeout(() => {
    p.timer = null;
    void runSave(id);
  }, SAVE_DEBOUNCE_MS);
  refreshSaveStatus();
}

/**
 * Absorb a header PUT's echo, keeping the fields the caller OWNS locally.
 *
 * The echo is authoritative for what the SERVER computed (bidValue) and stale for anything the user
 * has a newer local value for. That is two sets of keys, not one:
 *   - `sentKeys` — fields THIS request wrote (the trailing-space / mid-typed-char fix).
 *   - the still-pending header keys — fields edited AFTER this request was sent (coalesced into the
 *     NEXT save). This request's echo predates them, so absorbing them would revert a field the
 *     user just changed to a value the server has not been told about yet.
 * Everything else (bidValue, other server-side columns) is absorbed as before.
 */
function reconcileHeaderEcho(preserve: Set<keyof Bid>, stored: StoredBid | undefined): void {
  if (!stored?.bid) return;
  const j = cache.findIndex((b) => b.id === stored.bid.id);
  if (j < 0) return;
  const echoed: Partial<Bid> = { ...stored.bid };
  preserve.forEach((k) => delete echoed[k]);
  const next = cache.slice();
  next[j] = { ...cache[j], ...echoed }; // keep local workbook + owned/in-flight edits
  commit(next);
}

/** Reconcile the headline bidValue to the server-authoritative workbook total. */
function reconcileWorkbookTotal(id: string, serverTotal: number | undefined): void {
  if (serverTotal === undefined || !Number.isFinite(serverTotal)) return;
  const j = cache.findIndex((b) => b.id === id);
  if (j < 0) return;
  const next = cache.slice();
  next[j] = { ...cache[j], bidValue: serverTotal };
  commit(next);
}

/**
 * Drain a bid's pending edits, one request at a time.
 *
 * Guarded by `inFlight`: a second entry while a drain is running is a no-op, and
 * the running drain re-checks `hasWork` after every await, so edits queued mid-
 * flight are picked up by the SAME loop rather than a competing one. The tail
 * (final `hasWork` check → clear `inFlight`) runs with no `await` between, so no
 * other code can interleave to strand freshly-queued work.
 */
async function runSave(id: string): Promise<void> {
  const p = pendingFor(id);
  if (p.inFlight) return;
  if (!hasWork(p)) return;
  const uuid = idMap.get(id);
  if (!uuid) return;

  p.inFlight = true;
  setSaveStatus('saving');
  try {
    while (hasWork(p)) {
      // This drain now covers whatever is pending, so any debounce timer armed by a mid-flight
      // edit is redundant — clear it so it cannot fire a useless second run or leave the indicator
      // reporting "pending" after everything has saved.
      if (p.timer) {
        clearTimeout(p.timer);
        p.timer = null;
      }
      const header = p.header;
      const sentKeys = Object.keys(header) as (keyof Bid)[];
      p.header = {};
      const workbook = p.workbook;
      const workbookTotal = p.workbookTotal;
      p.workbook = null;
      p.workbookTotal = undefined;

      if (sentKeys.length > 0) {
        const stored = await api.patchBid(uuid, header);
        // Preserve what this request sent AND anything queued since (coalesced into the next save) —
        // this echo predates those later edits and must not roll them back.
        const preserve = new Set<keyof Bid>([...sentKeys, ...(Object.keys(p.header) as (keyof Bid)[])]);
        reconcileHeaderEcho(preserve, stored);
      }
      if (workbook) {
        const serverTotal = await api.saveWorkbook(uuid, workbook);
        reconcileWorkbookTotal(id, serverTotal);
      }
    }
    p.inFlight = false;
    // A save that failed elsewhere may have left another bid mid-error; only claim
    // success when this queue is genuinely drained.
    setSaveStatus('saved');
    refreshSaveStatus();
  } catch (err) {
    // The optimistic edit was never persisted — drop the outstanding pending write
    // and resync rather than keep showing work the server rejected.
    p.inFlight = false;
    p.header = {};
    p.workbook = null;
    p.workbookTotal = undefined;
    if (p.timer) {
      clearTimeout(p.timer);
      p.timer = null;
    }
    reportWriteFailure('save this bid', err);
    setSaveStatus('error');
    void refresh();
  }
}

/**
 * Force a bid's pending edits to save NOW (skip the debounce). Used when leaving
 * the editor — unmount, navigation, tab close — so nothing typed is lost. Returns
 * the in-flight promise (awaitable in tests; fire-and-forget from an unmount).
 */
export function flushBidSaves(id: string): Promise<void> {
  const p = saves.get(id);
  if (!p) return Promise.resolve();
  if (p.timer) {
    clearTimeout(p.timer);
    p.timer = null;
  }
  return runSave(id);
}

/**
 * True when a bid has edits that are NOT yet safely on the server: work still queued
 * (debouncing), or a request currently in flight. Drives the "unsaved changes?" prompt shown
 * when leaving the editor. A drained queue (everything written) reads as false.
 *
 * NOTE: this sees only what has reached the store. Typing that is still local to the editor
 * (not yet blurred into `setBidWorkbook`) is invisible here — callers that need to include it
 * must adopt that pending edit into the store first (BidDetailPage does).
 */
export function hasUnsavedChanges(id: string): boolean {
  const p = saves.get(id);
  if (!p) return false;
  return p.inFlight || hasWork(p);
}

/**
 * Throw away a bid's un-saved edits and resync from the server (the "Discard" choice).
 *
 * Cancels the debounce and drops everything still queued, then `refresh()`es: that replaces the
 * cache with the server's headers (reverting optimistic header edits) and clears cached workbooks
 * so re-opening the bid pulls the stored one. A request already in flight cannot be un-sent, but
 * anything queued behind it is dropped.
 */
export function discardBidChanges(id: string): void {
  const p = saves.get(id);
  if (p) {
    if (p.timer) {
      clearTimeout(p.timer);
      p.timer = null;
    }
    p.header = {};
    p.workbook = null;
    p.workbookTotal = undefined;
  }
  refreshSaveStatus();
  void refresh();
}

// ---------------------------------------------------------------------------

export function updateBid(id: string, patch: Partial<Bid>): void {
  const i = cache.findIndex((b) => b.id === id);
  // Ignore a no-op patch: if every field already equals what is cached, nothing was actually
  // edited, so there is nothing to save. Without this a re-render or a re-selection of the same
  // value would arm the debounce and flip the indicator to "Saving…" for a write that changes
  // nothing.
  if (i >= 0) {
    const current = cache[i];
    const changed = (Object.keys(patch) as (keyof Bid)[]).some((k) => current[k] !== patch[k]);
    if (!changed) return;
    const next = cache.slice();
    next[i] = { ...current, ...patch }; // optimistic
    commit(next);
  }
  const uuid = idMap.get(id);
  if (!uuid) return; // no server row yet (optimistic-only) — cache is the record
  // Merge into the pending header patch (field-by-field, last write wins) and (re)arm
  // the debounce. runSave later absorbs ONLY the fields it did NOT send from the echo,
  // which is what kept trailing spaces / mid-typed characters from being reverted by a
  // stale server echo (UpdateBidSchema's str_strip_whitespace).
  const p = pendingFor(id);
  p.header = { ...p.header, ...patch };
  scheduleSave(id);
}

/**
 * Convert a Won bid into a project. Resolves the bid-number the UI holds to the server UUID the
 * route addresses — the store owns that map, exactly as updateBid/deleteBid do.
 *
 * Calling the API with `bid.id` directly sends 'BID-2026-014' to a route that validates a UUID, so
 * every conversion came back 400 "Bid not found." on a bid plainly on screen. The mapping is the
 * whole reason this seam exists (see the header): components speak bid numbers, the API speaks
 * UUIDs.
 */
export async function convertBidToProject(
  id: string,
  opts: api.ConvertOptions = {},
): Promise<api.ConversionResult> {
  const uuid = idMap.get(id);
  if (!uuid) throw new Error('This bid is still loading. Try again in a moment.');
  const result = await api.convertToProject(uuid, opts);
  // The bid is now converted; re-read so it shows as such (and offers Open Project) on return.
  await refresh();
  return result;
}

export function deleteBid(id: string): void {
  const uuid = idMap.get(id);
  // Cancel any queued save for this bid — it is about to cease to exist.
  const p = saves.get(id);
  if (p?.timer) clearTimeout(p.timer);
  saves.delete(id);
  workbookLoadFailed.delete(id);
  commit(cache.filter((b) => b.id !== id));
  idMap.delete(id);
  refreshSaveStatus();
  if (!uuid) return;
  // Delete stays immediate (not debounced): it is a discrete action, not typing, and
  // there is nothing left to coalesce once the bid is gone.
  void api.removeBid(uuid).catch((err) => {
    reportWriteFailure('delete this bid', err);
    void refresh();
  });
}

/** Persist a bid's structured estimate. Keeps the headline bidValue in sync (server value wins). */
export function setBidWorkbook(id: string, workbook: BidWorkbook, bidTotal?: number): void {
  if (workbookLoadFailed.has(id)) {
    // The persisted workbook is UNKNOWN, not empty — writing now would replace it with whatever
    // the fallback editor holds. Belt-and-braces under the page's own Retry gate.
    console.warn('Refusing workbook write for', id, '— its last load failed; retry the load first.');
    return;
  }
  const patch: Partial<Bid> = { workbook };
  if (typeof bidTotal === 'number' && Number.isFinite(bidTotal)) patch.bidValue = bidTotal;
  const i = cache.findIndex((b) => b.id === id);
  if (i >= 0) {
    // Ignore a no-op save: if the workbook is structurally identical to what is cached, nothing was
    // edited (e.g. a blur after focusing a field without changing it), so do not save. The workbook
    // is plain data, so a stringify compare is a sound equality check and cheap at blur frequency.
    const current = cache[i];
    if (current.workbook && JSON.stringify(current.workbook) === JSON.stringify(workbook)) return;
    const next = cache.slice();
    next[i] = { ...current, ...patch }; // optimistic
    commit(next);
  }
  const uuid = idMap.get(id);
  if (!uuid) return; // no server row yet (optimistic-only) — cache is the record
  // Latest-wins: the workbook PUT is replace-all, so only the most recent copy matters.
  const p = pendingFor(id);
  p.workbook = workbook;
  p.workbookTotal = bidTotal;
  scheduleSave(id);
}

/**
 * The company's configured targetMargin as a 0..1 fraction, or undefined when there is none.
 *
 * The config param is PERCENT-as-number (18) — and `param_value` is stripped server-side for
 * financially-blind callers, so their lookup naturally finds nothing. A failed fetch, an absent
 * param, an unparseable or out-of-domain value all seed NOTHING: 0 reads as absence in the cost
 * resolution (4.3), so a fabricated 0% (or a clamped value) would mint a margin nobody set.
 */
async function configTargetMargin(): Promise<number | undefined> {
  try {
    const params = await listCatalog('/bid-config-defaults', true);
    const raw = params.find((p) => p.param_key === 'targetMargin')?.param_value;
    const pct = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
    return Number.isFinite(pct) && pct > 0 && pct <= 100 ? pct / 100 : undefined;
  } catch {
    return undefined; // config unavailable — 4.3's read-time fallback prices costing instead
  }
}

/** Create a blank draft on the server and return its bid-number (for navigation). */
export async function createDraftBid(): Promise<string> {
  // Seeded at POST-time, not by a later effect: nothing can overwrite what a user then types,
  // and a draft with no company default simply carries no margin (the honest dash).
  const targetMargin = await configTargetMargin();
  // No profitPct: a blank draft has none of its own, so the server applies the tenant default.
  const stored = await api.createBid(
    { status: 'Draft', ...(targetMargin !== undefined && { targetMargin }) },
    { rowLengthM: 0 },
  );
  // Seed an empty workbook so the detail view opens ready-to-edit without a second fetch — using
  // the profit the SERVER assigned, not a hardcoded 30 that would misreport a configured default.
  // From stored.profitPct: apiToStored puts the server's profit THERE, never on bid.workbook —
  // the old `stored.bid.workbook?.profitPct` was always undefined, so every fresh draft priced
  // profit at 0 until a reload (gap review 0.3).
  absorb({
    ...stored,
    bid: {
      ...stored.bid,
      workbook: { rowLengthM: 0, profitPct: stored.profitPct, phases: [] },
    },
  });
  return stored.bid.id;
}

export function formatBidMoney(n: number | undefined): string {
  // Delegates so redaction (blind caller, or a figure the server omitted) is decided in ONE place.
  return money(n);
}
